import { kanbanItemKey } from './kanbanFilterItems';

/**
 * Selecionar VÁRIAS justificativas e arrastá-las de uma vez (K11 da Gisele).
 *
 * Na configuração do Kanban, cada justificativa é arrastada sozinha. Montar
 * uma coluna com dez delas são dez arrastos — e as colunas dela têm muitas,
 * que é justamente a queixa que originou este pedido.
 *
 * ## A decisão central: a seleção pertence a UMA coluna
 *
 * Selecionar itens em colunas diferentes e arrastar tudo junto é possível de
 * escrever e ruim de usar: o arrasto sairia de vários lugares ao mesmo tempo,
 * e desfazer exigiria lembrar de onde cada um veio. Pior, a coluna de origem
 * decide o que acontece — sair de "disponíveis" é COPIAR para a coluna, sair
 * de uma coluna é MOVER. Misturar as duas semânticas num arrasto só produz um
 * resultado que ninguém consegue prever olhando a tela.
 *
 * Então clicar num item de outra coluna começa uma seleção nova ali. É a
 * regra que o teste mais protege.
 *
 * ## Por que um módulo próprio
 *
 * O `AdvancedFilterModal` tem mais de duas mil linhas e nenhum teste. Toda a
 * lógica que dá para tirar de lá vira algo que se pode afirmar.
 */

/** A seleção vazia. */
export const SELECAO_VAZIA = { colunaId: null, chaves: [] };

/**
 * Liga ou desliga um item da seleção.
 *
 * @param {{colunaId: string|null, chaves: string[]}} selecao
 * @param {string} colunaId  de onde o item saiu ('available' é a lista de
 *   justificativas ainda não usadas)
 * @param {object} item  `{status_id, substatus_id}`
 */
export const alternarSelecao = (selecao, colunaId, item) => {
    const chave = kanbanItemKey(item);

    if (!chave || colunaId == null) return selecao;

    const atual = selecao || SELECAO_VAZIA;

    // Outra coluna: recomeça. Ver o porquê no cabeçalho -- juntar origens
    // diferentes misturaria "copiar" com "mover".
    if (atual.colunaId !== colunaId) {
        return { colunaId, chaves: [chave] };
    }

    const jaTem = atual.chaves.includes(chave);
    const chaves = jaTem
        ? atual.chaves.filter((c) => c !== chave)
        : [...atual.chaves, chave];

    // Desmarcar o último devolve a seleção ao vazio, com a coluna zerada: do
    // contrário a coluna continuaria "dona" de uma seleção sem itens, e o
    // próximo clique em OUTRA coluna pareceria não fazer nada.
    return chaves.length === 0 ? SELECAO_VAZIA : { colunaId, chaves };
};

/** O item está selecionado? */
export const estaSelecionado = (selecao, colunaId, item) => {
    const chave = kanbanItemKey(item);

    if (!chave) return false;

    const atual = selecao || SELECAO_VAZIA;

    return atual.colunaId === colunaId && atual.chaves.includes(chave);
};

/**
 * O que um arrasto deve levar.
 *
 * Arrastar um item SELECIONADO leva a seleção inteira. Arrastar um item de
 * fora dela leva só ele — e descarta a seleção, porque a pessoa mostrou que
 * mudou de ideia. Sem essa segunda regra, um arrasto comum depois de uma
 * seleção esquecida moveria itens que ninguém estava olhando.
 *
 * @returns {object[]} os itens a mover, sempre com ao menos um
 */
export const itensParaArrastar = (selecao, colunaId, item, itensDaColuna) => {
    if (!estaSelecionado(selecao, colunaId, item)) {
        return [item];
    }

    const porChave = new Map(
        (Array.isArray(itensDaColuna) ? itensDaColuna : []).map((it) => [kanbanItemKey(it), it]),
    );

    // A ordem segue a da COLUNA, não a de clique: é a ordem que a pessoa vê
    // na tela, e é como os itens vão aparecer no destino.
    const naOrdemDaColuna = (Array.isArray(itensDaColuna) ? itensDaColuna : [])
        .filter((it) => selecao.chaves.includes(kanbanItemKey(it)));

    if (naOrdemDaColuna.length > 0) return naOrdemDaColuna;

    // Reserva: a coluna não foi informada. Devolve pelas chaves, na ordem de
    // clique, para o arrasto não virar um nada.
    return selecao.chaves
        .map((c) => porChave.get(c))
        .filter(Boolean)
        .concat(porChave.size === 0 ? [item] : []);
};

/** Quantos itens o próximo arrasto leva -- para o rótulo na tela. */
export const quantidadeSelecionada = (selecao, colunaId) => {
    const atual = selecao || SELECAO_VAZIA;

    return atual.colunaId === colunaId ? atual.chaves.length : 0;
};
